import type {
  DashboardChildNavItem,
  DashboardInfo,
  DashboardVariant,
} from "./types";

function resolveDashboardVariant(
  child: DashboardChildNavItem,
  parent: DashboardInfo
): DashboardVariant {
  const fromChild = child.dashboard_variant;
  if (fromChild === "CUSTOM" || fromChild === "SUPERSET") {
    return fromChild;
  }
  return "SUPERSET";
}

export function resolveChildSection(
  child: DashboardChildNavItem,
  parentSection: string,
): string {
  const trimmed = child.section?.trim();
  return trimmed || parentSection;
}

function toDashboardInfoFromChild(
  child: DashboardChildNavItem,
  inheritedSection: string,
  parent: DashboardInfo
): DashboardInfo {
  return {
    id: child.id,
    superset_dashboard_id: String(child.superset_dashboard_id ?? ""),
    dashboard_uuid: String(child.dashboard_uuid ?? ""),
    section: inheritedSection,
    title: child.title,
    dashboard_variant: resolveDashboardVariant(child, parent),
    updated_at: child.updated_at ?? "",
    charts: child.charts,
    hide_chart_controls: child.hide_chart_controls,
    parent_dashboard: child.parent_dashboard,
    child_dashboards: undefined,
  };
}

export function findDashboardById(
  dashboards: DashboardInfo[],
  id: number
): DashboardInfo | undefined {
  for (const d of dashboards) {
    if (d.id === id) {
      return d;
    }
    if (!d.child_dashboards?.length) {
      continue;
    }
    const parentSection = d.section;
    for (const child of d.child_dashboards) {
      if (child.id === id) {
        return toDashboardInfoFromChild(
          child,
          resolveChildSection(child, parentSection),
          d,
        );
      }
    }
  }
  return undefined;
}

export function getSectionDashboardsForMenu(
  dashboards: DashboardInfo[],
  section: string,
): DashboardInfo[] {
  const targetSection = section.trim();
  if (!targetSection) {
    return [];
  }

  const topLevel = dashboards.filter((d) => d.section === targetSection);
  if (topLevel.length > 0) {
    return topLevel;
  }

  const childDashboards: DashboardInfo[] = [];
  for (const parent of dashboards) {
    if (!parent.child_dashboards?.length) {
      continue;
    }
    for (const child of parent.child_dashboards) {
      if (resolveChildSection(child, parent.section) !== targetSection) {
        continue;
      }
      childDashboards.push(
        toDashboardInfoFromChild(child, targetSection, parent),
      );
    }
  }

  return childDashboards.sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
  );
}
